import type { AdminCaseView } from '@repo/shared'

/** 순서 계산에 쓰는 칸만 */
type Orderable = Pick<
  AdminCaseView,
  'id' | 'language' | 'adminRank' | 'sortOrder' | 'isHidden' | 'isDeleted'
>

/**
 * 갤러리 순서 (설계 6.1 과 같은 규칙): 관리자 순위가 있는 것 먼저(작은 수가 위), 그다음 엑셀 줄 순서.
 * 둘 다 같으면 0 을 돌려 서버가 준 순서(created_at 최신, id)를 그대로 둔다. 배열 sort 는 안정 정렬이다.
 * 그래서 서버 목록 순서를 바꾸지 않은 배열에 써야 한다 (casesApi.listAll 결과)
 */
export function compareCaseOrder(a: Orderable, b: Orderable): number {
  if (a.adminRank !== null || b.adminRank !== null) {
    if (a.adminRank === null) return 1
    if (b.adminRank === null) return -1
    if (a.adminRank !== b.adminRank) return a.adminRank - b.adminRank
  }
  return a.sortOrder - b.sortOrder
}

/** 앱 갤러리에 실제로 보이는 사례 (숨김·내림 제외) */
export const isInGallery = (c: Orderable) => !c.isHidden && !c.isDeleted

/** 한 언어의 갤러리 줄 (앱이 보이는 순서 그대로). 순위는 이 줄 안에서 정한다 (설계 7장 '같은 언어 안에서') */
export function galleryLine<T extends Orderable>(items: T[], language: string): T[] {
  return items.filter((c) => c.language === language && isInGallery(c)).sort(compareCaseOrder)
}

export type RankMove = 'up' | 'down' | 'pin' | 'clear'

/** 서버에 보낼 순위 바꾸기 한 건 */
export interface RankChange {
  id: string
  adminRank: number | null
}

/** 순위 칸 상한 (서버 adminCasePatchSchema 와 같은 값) */
const MAX_RANK = 9999

/**
 * 순위 이동을 서버 PATCH 목록으로 바꾼다. 가능한 한 1~2건만 보낸다 (건마다 작업 기록이 남는다).
 * - up·down: 순위가 있는 사례끼리 자리를 바꾼다. 순위 값이 같으면 그 언어의 순위 줄을 1부터 다시 매긴다
 * - pin: 순위가 없는 사례에 '순위 있는 줄의 맨 끝' 순위를 준다. 엑셀 순서 사이를 한 칸씩 옮기려면
 *   그 위의 모든 사례에 순위를 매겨야 해서, 한 번에 순위 줄 끝으로 올린다
 * - clear: 순위를 지워 엑셀 순서로 돌린다
 * 할 수 없는 이동(맨 위에서 위로 등)이면 빈 배열
 */
export function planRankMove<T extends Orderable>(
  items: T[],
  id: string,
  move: RankMove,
): RankChange[] {
  const target = items.find((c) => c.id === id)
  if (!target) return []
  if (move === 'clear') return target.adminRank === null ? [] : [{ id, adminRank: null }]

  if (move === 'pin') {
    if (target.adminRank !== null) return []
    // 숨긴 사례의 순위와도 겹치지 않게 같은 언어 전체(내림 제외)에서 가장 큰 순위 다음 값을 준다
    const ranks = items
      .filter((c) => c.language === target.language && !c.isDeleted && c.adminRank !== null)
      .map((c) => c.adminRank as number)
    const next = ranks.length ? Math.max(...ranks) + 1 : 1
    return next > MAX_RANK ? [] : [{ id, adminRank: next }]
  }

  if (!isInGallery(target) || target.adminRank === null) return []
  const ranked = galleryLine(items, target.language).filter((c) => c.adminRank !== null)
  const i = ranked.findIndex((c) => c.id === id)
  const j = move === 'up' ? i - 1 : i + 1
  if (i < 0 || j < 0 || j >= ranked.length) return []
  const other = ranked[j] as T

  if (other.adminRank !== target.adminRank) {
    return [
      { id: target.id, adminRank: other.adminRank },
      { id: other.id, adminRank: target.adminRank },
    ]
  }
  // 같은 순위 값이 겹쳐 있으면 바꿔도 순서가 그대로다. 새 순서로 1부터 다시 매기고 바뀐 것만 보낸다
  const reordered = [...ranked]
  reordered[i] = other
  reordered[j] = target
  return reordered
    .map((c, k) => ({ id: c.id, adminRank: k + 1, before: c.adminRank }))
    .filter((r) => r.adminRank !== r.before)
    .map(({ id: rid, adminRank }) => ({ id: rid, adminRank }))
}
